/**
 * Service to manage garment-specific templates for size charts
 */

// Define available garment types
export const garmentTypes = [
  // Single garments
  { value: 'dress', label: 'Dress' },
  { value: 'top', label: 'Top/Shirt' },
  { value: 'pants', label: 'Pants/Trousers' },
  { value: 'skirt', label: 'Skirt' },
  { value: 'jacket', label: 'Jacket/Coat' },
  { value: 'shoes', label: 'Shoes' },
  { value: 'accessories', label: 'Accessories' },
  
  // New garment types
  { value: 'romper', label: 'Romper/Jumpsuit' },
  { value: 'swimwear', label: 'Swimwear' },
  
  // Underwear and lingerie
  { value: 'mens_underwear', label: "Men's Underwear" },
  { value: 'womens_underwear', label: "Women's Underwear/Panties" },
  { value: 'bra', label: 'Bra' },
  { value: 'lingerie_set', label: 'Lingerie Set (Bra & Underwear)' },
  { value: 'shapewear', label: 'Shapewear' },
  
  // Sets and combinations
  { value: 'two_piece_set', label: '2-Piece Set (Top & Bottom)' },
  { value: 'two_piece_skirt_set', label: '2-Piece Set (Top & Skirt)' },
  { value: 'three_piece_set', label: '3-Piece Set (Top, Bottom & Jacket)' },
  { value: 'suit', label: 'Suit (Jacket & Pants)' },
  { value: 'tracksuit', label: 'Tracksuit' },
];

// Define measurement points for each garment type
export const measurementPoints = {
  dress: [
    { id: 'bust', label: 'Bust', description: 'Measure from the stitches below the armpits on one side to another.' },
    { id: 'waist', label: 'Waist', description: 'Measure straight across the narrowest waist line from edge to edge.' },
    { id: 'hip', label: 'Hip', description: 'Measure straight across the widest hip line from edge to edge.' },
    { id: 'length', label: 'Length', description: 'Measure from where the shoulder seam meets the collar to the hem.' },
  ],
  top: [
    { id: 'bust', label: 'Chest/Bust', description: 'Measure from the stitches below the armpits on one side to another.' },
    { id: 'shoulder', label: 'Shoulder', description: 'Measure from one shoulder edge to the other.' },
    { id: 'sleeve', label: 'Sleeve Length', description: 'Measure from the shoulder seam to the end of the sleeve.' },
    { id: 'length', label: 'Length', description: 'Measure from the highest point of the shoulder to the bottom hem.' },
  ],
  pants: [
    { id: 'waist', label: 'Waist', description: 'Measure around the natural waistline.' },
    { id: 'hip', label: 'Hip', description: 'Measure around the fullest part of your hips.' },
    { id: 'inseam', label: 'Inseam', description: 'Measure from the crotch to the bottom of the leg.' },
    { id: 'outseam', label: 'Outseam', description: 'Measure from the top of the waistband to the bottom of the leg.' },
  ],
  skirt: [
    { id: 'waist', label: 'Waist', description: 'Measure around the natural waistline.' },
    { id: 'hip', label: 'Hip', description: 'Measure around the fullest part of your hips.' },
    { id: 'length', label: 'Length', description: 'Measure from the top of the waistband to the hem.' },
  ],
  jacket: [
    { id: 'chest', label: 'Chest', description: 'Measure from the stitches below the armpits on one side to another.' },
    { id: 'shoulder', label: 'Shoulder', description: 'Measure from one shoulder edge to the other.' },
    { id: 'sleeve', label: 'Sleeve Length', description: 'Measure from the shoulder seam to the end of the sleeve.' },
    { id: 'length', label: 'Length', description: 'Measure from the highest point of the shoulder to the bottom hem.' },
  ],
  shoes: [
    { id: 'length', label: 'Foot Length', description: 'Measure from the heel to the tip of the longest toe.' },
    { id: 'width', label: 'Foot Width', description: 'Measure the widest part of your foot.' },
  ],
  accessories: [
    { id: 'circumference', label: 'Circumference', description: 'Measure around the widest part.' },
    { id: 'length', label: 'Length', description: 'Measure from end to end.' },
  ],
  
  // New garment types
  romper: [
    { id: 'bust', label: 'Bust', description: 'Measure from the stitches below the armpits on one side to another.' },
    { id: 'waist', label: 'Waist', description: 'Measure straight across the narrowest waist line from edge to edge.' },
    { id: 'hip', label: 'Hip', description: 'Measure straight across the widest hip line from edge to edge.' },
    { id: 'inseam', label: 'Inseam', description: 'Measure from the crotch to the bottom of the leg.' },
    { id: 'length', label: 'Total Length', description: 'Measure from the highest point of the shoulder to the bottom hem.' },
  ],
  
  swimwear: [
    { id: 'bust', label: 'Bust', description: 'Measure from the stitches below the armpits on one side to another.' },
    { id: 'waist', label: 'Waist', description: 'Measure straight across the narrowest waist line from edge to edge.' },
    { id: 'hip', label: 'Hip', description: 'Measure straight across the widest hip line from edge to edge.' },
  ],
  
  mens_underwear: [
    { id: 'waist', label: 'Waist', description: 'Measure around the natural waistline where the underwear sits.' },
    { id: 'hip', label: 'Hip', description: 'Measure around the fullest part of your hips.' },
    { id: 'thigh', label: 'Thigh', description: 'Measure around the fullest part of your thigh.' },
  ],
  
  womens_underwear: [
    { id: 'waist', label: 'Waist', description: 'Measure around the natural waistline.' },
    { id: 'hip', label: 'Hip', description: 'Measure around the fullest part of your hips.' },
    { id: 'rise', label: 'Rise', description: 'Measure from the center of the crotch up to the top of the waistband.' },
  ],
  
  bra: [
    { id: 'band', label: 'Band Size', description: 'Measure directly under your bust, around your ribcage. Round to the nearest whole number.' },
    { id: 'bust', label: 'Bust/Cup Size', description: 'Measure around the fullest part of your bust while wearing a non-padded bra.' },
    { id: 'overbust', label: 'Overbust', description: 'Measure around your chest above your bust, under your armpits.' },
  ],
  
  lingerie_set: [
    { id: 'band', label: 'Bra - Band Size', description: 'Measure directly under your bust, around your ribcage.' },
    { id: 'bust', label: 'Bra - Bust/Cup Size', description: 'Measure around the fullest part of your bust while wearing a non-padded bra.' },
    { id: 'waist', label: 'Underwear - Waist', description: 'Measure around the natural waistline.' },
    { id: 'hip', label: 'Underwear - Hip', description: 'Measure around the fullest part of your hips.' },
  ],
  
  shapewear: [
    { id: 'bust', label: 'Bust', description: 'Measure around the fullest part of your bust.' },
    { id: 'waist', label: 'Waist', description: 'Measure around the narrowest part of your waist.' },
    { id: 'hip', label: 'Hip', description: 'Measure around the fullest part of your hips.' },
    { id: 'thigh', label: 'Thigh', description: 'For full-body shapewear, measure around the fullest part of your thigh.' },
  ],
  
  // Sets and combinations
  two_piece_set: [
    { id: 'bust', label: 'Top - Bust', description: 'Measure from the stitches below the armpits on one side to another.' },
    { id: 'top_length', label: 'Top - Length', description: 'Measure from the highest point of the shoulder to the bottom hem.' },
    { id: 'waist', label: 'Bottom - Waist', description: 'Measure around the natural waistline.' },
    { id: 'hip', label: 'Bottom - Hip', description: 'Measure around the fullest part of your hips.' },
    { id: 'inseam', label: 'Bottom - Inseam', description: 'Measure from the crotch to the bottom of the leg.' },
  ],
  
  two_piece_skirt_set: [
    { id: 'bust', label: 'Top - Bust', description: 'Measure from the stitches below the armpits on one side to another.' },
    { id: 'top_length', label: 'Top - Length', description: 'Measure from the highest point of the shoulder to the bottom hem.' },
    { id: 'waist', label: 'Skirt - Waist', description: 'Measure around the natural waistline.' },
    { id: 'hip', label: 'Skirt - Hip', description: 'Measure around the fullest part of your hips.' },
    { id: 'skirt_length', label: 'Skirt - Length', description: 'Measure from the top of the waistband to the hem.' },
  ],
  
  three_piece_set: [
    { id: 'bust', label: 'Top - Bust', description: 'Measure from the stitches below the armpits on one side to another.' },
    { id: 'top_length', label: 'Top - Length', description: 'Measure from the highest point of the shoulder to the bottom hem.' },
    { id: 'waist', label: 'Bottom - Waist', description: 'Measure around the natural waistline.' },
    { id: 'hip', label: 'Bottom - Hip', description: 'Measure around the fullest part of your hips.' },
    { id: 'jacket_chest', label: 'Jacket - Chest', description: 'Measure from the stitches below the armpits on one side to another.' },
    { id: 'jacket_length', label: 'Jacket - Length', description: 'Measure from the highest point of the shoulder to the bottom hem.' },
  ],
  
  suit: [
    { id: 'chest', label: 'Jacket - Chest', description: 'Measure from the stitches below the armpits on one side to another.' },
    { id: 'shoulder', label: 'Jacket - Shoulder', description: 'Measure from one shoulder edge to the other.' },
    { id: 'sleeve', label: 'Jacket - Sleeve', description: 'Measure from the shoulder seam to the end of the sleeve.' },
    { id: 'waist', label: 'Pants - Waist', description: 'Measure around the natural waistline.' },
    { id: 'inseam', label: 'Pants - Inseam', description: 'Measure from the crotch to the bottom of the leg.' },
  ],
  
  tracksuit: [
    { id: 'chest', label: 'Top - Chest', description: 'Measure from the stitches below the armpits on one side to another.' },
    { id: 'sleeve', label: 'Top - Sleeve', description: 'Measure from the shoulder seam to the end of the sleeve.' },
    { id: 'waist', label: 'Bottom - Waist', description: 'Measure around the natural waistline.' },
    { id: 'inseam', label: 'Bottom - Inseam', description: 'Measure from the crotch to the bottom of the leg.' },
  ],
};

// Define fit types
export const fitTypes = [
  { value: 'slim', label: 'Slim Fit' },
  { value: 'regular', label: 'Regular Fit' },
  { value: 'loose', label: 'Loose Fit' },
];

// Define stretch levels
export const stretchLevels = [
  { value: 'none', label: 'No stretch' },
  { value: 'slight', label: 'Slightly stretchy' },
  { value: 'moderate', label: 'Moderate stretch' },
  { value: 'stretchy', label: 'Stretchy' },
  { value: 'high', label: 'Highly stretchy' },
];

// Get image path for a garment type
export const getGarmentImagePath = (garmentType) => {
  return `/images/sizecharts/${garmentType}.png`;
};

// Analyze material composition to determine stretch level
export const analyzeStretchLevel = (materials) => {
  if (!materials) return 'none';
  
  const materialsLower = materials.toLowerCase();
  
  // High stretch materials
  if (
    materialsLower.includes('elastane') ||
    materialsLower.includes('spandex') ||
    materialsLower.includes('lycra')
  ) {
    // Check percentage if available
    const percentageMatch = materialsLower.match(/(\d+)%\s*(elastane|spandex|lycra)/i);
    if (percentageMatch) {
      const percentage = parseInt(percentageMatch[1]);
      if (percentage >= 10) return 'high';
      if (percentage >= 5) return 'stretchy';
      if (percentage >= 2) return 'moderate';
      return 'slight';
    }
    return 'moderate'; // Default if percentage not found
  }
  
  // Moderate stretch materials
  if (
    materialsLower.includes('jersey') ||
    materialsLower.includes('knit') ||
    materialsLower.includes('stretch')
  ) {
    return 'moderate';
  }
  
  // Slight stretch materials
  if (
    materialsLower.includes('polyester') ||
    materialsLower.includes('rayon') ||
    materialsLower.includes('viscose')
  ) {
    return 'slight';
  }
  
  // No stretch materials
  if (
    materialsLower.includes('cotton') ||
    materialsLower.includes('linen') ||
    materialsLower.includes('silk') ||
    materialsLower.includes('wool') ||
    materialsLower.includes('denim')
  ) {
    // Check if it's a blend with stretch materials
    if (
      materialsLower.includes('elastane') ||
      materialsLower.includes('spandex') ||
      materialsLower.includes('lycra') ||
      materialsLower.includes('stretch')
    ) {
      return 'slight';
    }
    return 'none';
  }
  
  return 'none'; // Default
};

// Analyze product description to determine fit type
export const analyzeFitType = (description) => {
  if (!description) return 'regular';
  
  const descriptionLower = description.toLowerCase();
  
  if (
    descriptionLower.includes('slim fit') ||
    descriptionLower.includes('fitted') ||
    descriptionLower.includes('skinny') ||
    descriptionLower.includes('tight')
  ) {
    return 'slim';
  }
  
  if (
    descriptionLower.includes('loose fit') ||
    descriptionLower.includes('relaxed') ||
    descriptionLower.includes('oversized') ||
    descriptionLower.includes('baggy')
  ) {
    return 'loose';
  }
  
  return 'regular'; // Default
};
